// Central content model — business data kept separate from presentation.
// V1 verticals in priority order. "Business Consulting" is intentionally
// registered as a future vertical (not marketed in V1 UI).

export const VERTICALS = [
  {
    id: "people-hr",
    index: "01",
    name: "People & HR Solutions",
    short: "People & HR",
    tagline: "Build teams that compound.",
    description:
      "End-to-end talent acquisition and HR advisory for organisations that treat hiring as a strategic function, not a stopgap.",
    accent: "#2563EB",
    audience: "employers",
    image:
      "https://images.pexels.com/photos/12903168/pexels-photo-12903168.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    services: [
      { name: "Recruitment", icon: "UserSearch", slug: "recruitment" },
      { name: "Candidate Sourcing", icon: "Radar", slug: "candidate-sourcing" },
      { name: "RPO", icon: "Building2", slug: "rpo" },
      { name: "HR Consulting", icon: "ClipboardCheck", slug: "hr-consulting" },
      { name: "Workforce Planning", icon: "Network", slug: "workforce-planning" },
      { name: "Talent Strategy", icon: "Target", slug: "talent-strategy" },
    ],
  },
  {
    id: "career",
    index: "02",
    name: "Career Solutions",
    short: "Career",
    tagline: "Make your next move deliberate.",
    description:
      "Personalised career support for professionals who want their story, positioning and search strategy to work as hard as they do.",
    accent: "#0EA5E9",
    audience: "candidates",
    image:
      "https://images.pexels.com/photos/30692588/pexels-photo-30692588.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    services: [
      { name: "Resume Building", icon: "FileText", slug: "resume-building" },
      { name: "Resume Optimisation / ATS Review", icon: "ScanLine", slug: "resume-optimisation-ats-review" },
      { name: "LinkedIn Optimisation", icon: "Linkedin", slug: "linkedin-optimisation" },
      { name: "Interview Preparation", icon: "MessagesSquare", slug: "interview-preparation" },
      { name: "Career Consultation", icon: "Compass", slug: "career-consultation" },
      { name: "Professional Branding", icon: "BadgeCheck", slug: "professional-branding" },
      { name: "Job Search Strategy", icon: "Route", slug: "job-search-strategy" },
      { name: "Cover Letter / Application Support", icon: "Mail", slug: "cover-letter-application-support" },
    ],
  },
  {
    id: "ai-digital",
    index: "03",
    name: "AI & Digital Solutions",
    short: "AI & Digital",
    tagline: "A digital presence that does the work.",
    description:
      "Modern websites — built conventionally or accelerated with AI — designed for businesses that need to show up credibly online.",
    accent: "#1E293B",
    audience: "employers",
    image:
      "https://images.unsplash.com/photo-1684503830683-108f3e0fd03f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHxkaWdpdGFsJTIwdGVjaCUyMG1pbmltYWwlMjBzY3JlZW4lMjBhYnN0cmFjdHxlbnwwfHx8fDE3ODcxNTYyOTh8MA&ixlib=rb-4.1.0&q=85",
    services: [
      { name: "Business Website Development", icon: "LayoutTemplate", slug: "business-website-development" },
      { name: "AI-Powered Website Development", icon: "Sparkles", slug: "ai-powered-website-development" },
    ],
  },
];

// Maps a vertical to the most relevant problem-first path (for prefilled CTAs).
export const PATH_BY_VERTICAL = { "people-hr": "employer", career: "candidate", "ai-digital": "employer" };

export function getServiceBySlug(slug) {
  for (const vertical of VERTICALS) {
    const service = vertical.services.find((s) => s.slug === slug);
    if (service) return { service, vertical };
  }
  return null;
}

// Future vertical — architecturally present, deliberately not marketed in V1.
export const FUTURE_VERTICALS = [
  { id: "business-consulting", name: "Business Consulting", status: "coming-soon" },
];

export const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services", hasMega: true },
  { label: "For Employers", to: "/for-employers" },
  { label: "For Candidates", to: "/for-candidates" },
  { label: "About SCC", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export const PATHS = [
  {
    id: "employer",
    label: "Business / Employer",
    blurb: "I want to hire, build a team, or fix how we attract talent.",
    icon: "Building2",
    focusAreas: ["Recruitment", "RPO", "HR Consulting", "Workforce Planning", "Talent Strategy", "Website / Digital"],
  },
  {
    id: "candidate",
    label: "Professional / Candidate",
    blurb: "I want to grow my career, get noticed, and land the right role.",
    icon: "User",
    focusAreas: ["Resume & ATS", "LinkedIn", "Interview Prep", "Career Consultation", "Job Search Strategy", "Personal Branding"],
  },
  {
    id: "freelancer",
    label: "Freelancer / Consultant",
    blurb: "I run independently and need positioning, presence or process support.",
    icon: "Briefcase",
    focusAreas: ["Personal Branding", "Website / Digital", "Career Consultation", "Other"],
  },
];

export const HOW_IT_WORKS = [
  { index: "01", title: "Tell us what you need", body: "Start from your problem — not a service menu. We identify whether you're an employer, a professional, or an independent." },
  { index: "02", title: "We map the deliberate path", body: "A focused conversation to understand context, constraints and the outcome that actually matters to you." },
  { index: "03", title: "We assemble the right solution", body: "The relevant mix of People & HR, Career, or AI & Digital capability — nothing you don't need." },
  { index: "04", title: "We deliver and stay accountable", body: "Clear ownership, honest timelines, and support that continues after the handover." },
];

export const WHY_SCC = [
  { title: "Independent by design", body: "Not a recruitment desk, not a personal brand. An independent consulting company with a point of view." },
  { title: "Problem-first, always", body: "We start from what you're trying to solve, then bring the capability — never the other way around." },
  { title: "Deliberate, not default", body: "Every recommendation is chosen on purpose. No filler, no vanity work, no one-size template." },
  { title: "One partner, three fronts", body: "People & HR, Career, and AI & Digital under a single accountable relationship." },
];
